import type { Router, State } from "@real-router/core";

/** Captured like the deciding seven, but this one BUILDS the guarantee (#2072). */
const objectCreate = Object.create;

const DEFAULT_STORAGE_KEY = "real-router:scroll";

// Bounded retry budget for resolving a late-mounting scroll container on the
// restore path. A per-route container (e.g. an `overflow:auto` div rendered
// only on one route) can be committed to the DOM a few frames after the
// navigation settles — heavier routes paint later than the subscribe's rAF.
// ~10 frames (≈160ms at 60fps) comfortably covers a React commit of a large
// route without being perceptible. See the doc-block on `restorePos`.
const RESTORE_RETRY_FRAMES = 10;

const NOOP_INSTANCE: { destroy: () => void } = Object.freeze({
  destroy: () => {
    /* no-op */
  },
});

export type ScrollRestorationMode = "restore" | "top" | "native";

export interface ScrollRestorationOptions {
  mode?: ScrollRestorationMode | undefined;
  anchorScrolling?: boolean | undefined;
  scrollContainer?: (() => HTMLElement | null) | undefined;
  /**
   * Scroll behavior passed to `scrollTo({ behavior })` and
   * `scrollIntoView({ behavior })`.
   *
   * - `"auto"` (default) — browser-defined, usually instant.
   * - `"instant"` — explicit instant jump (no animation).
   * - `"smooth"` — animated transition. Note: smooth restore on back/traverse
   *   can feel disorienting if the user expects to land at the saved position
   *   immediately. Recommended for `mode: "top"` or anchor scroll only.
   *
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/API/ScrollToOptions/behavior).
   */
  behavior?: ScrollBehavior | undefined;
  /**
   * sessionStorage key for persisting saved scroll positions. Default:
   * `"real-router:scroll"`. Override only when multiple independent
   * `RouterProvider` instances share the same document and you need to
   * isolate their scroll stores (e.g. micro-frontends, embedded widgets,
   * or testing). For a single app with one provider the default is fine.
   */
  storageKey?: string | undefined;
}

interface NavigationContext {
  direction?: "forward" | "back" | "unknown";
  navigationType?: "push" | "replace" | "traverse" | "reload";
}

export function createScrollRestoration(
  router: Router,
  options?: ScrollRestorationOptions,
): { destroy: () => void } {
  if (typeof globalThis.window === "undefined") {
    return NOOP_INSTANCE;
  }

  const mode = options?.mode ?? "restore";

  // mode "native" = utility does nothing. Don't flip history.scrollRestoration,
  // don't subscribe, don't register pagehide — `history.scrollRestoration`
  // stays at the browser default ("auto") so the browser handles scroll
  // restore natively. (Note: this is the OPPOSITE of `history.scrollRestoration
  // === "manual"` — utility's "native" leaves the DOM property at "auto" so
  // the browser is in charge.)
  if (mode === "native") {
    return NOOP_INSTANCE;
  }

  const anchorEnabled = options?.anchorScrolling ?? true;
  const getContainer = options?.scrollContainer;
  const behavior: ScrollBehavior = options?.behavior ?? "auto";
  const storageKey = options?.storageKey ?? DEFAULT_STORAGE_KEY;

  // Write-through in-memory cache: parse sessionStorage once per provider
  // mount, then mutate in-memory. Avoids a JSON.parse + JSON.stringify pair
  // on every subscribeLeave / pagehide event.
  let store: Record<string, number> | undefined;

  const loadStore = (): Record<string, number> => {
    if (store !== undefined) {
      return store;
    }

    // ⚑ A PROTOTYPE-LESS record, and here that is the cheap fix rather than the
    // expensive one (#1852). The key is `${route}:${json}`, so both the
    // skip-same-value READ and the write below consult a chain under a name the
    // page never chose; with no chain there is nothing to consult. Core pays for
    // the same guarantee with `putField` because its bags are read on every
    // render and V8 keeps a prototype-less object in dictionary mode — this one
    // is a small per-mount cache read a few times per navigation, so the tax is
    // expected to be negligible and no primitive needs importing.
    //
    // ⚠ "Expected", not measured, and the word is chosen: nothing benches this
    // cache. Its sibling in `putField`'s docblock is the reason to be careful —
    // "not measurable" there turned out to be a statement about the instrument,
    // not the cost. Measure before repeating the word.
    //
    // ⚠ `JSON.parse` DEFINES, so a stored `"__proto__"` key arrives as ordinary
    // data either way; what this closes is the ambient half.
    try {
      const raw = sessionStorage.getItem(storageKey);
      const parsed = raw
        ? (JSON.parse(raw) as Record<string, number>)
        : undefined;

      store = Object.assign(
        objectCreate(null) as Record<string, number>,
        parsed,
      );
    } catch {
      store = objectCreate(null) as Record<string, number>;
    }

    return store;
  };

  const putPos = (key: string, pos: number): void => {
    try {
      const cached = loadStore();

      // Skip-same-value: when a route is left at the same scroll position it
      // already holds in the cache (e.g. tab-switching without scrolling),
      // both the in-memory write and the JSON.stringify + setItem pair are
      // no-ops. Eliminates redundant serialization on the navigation hot
      // path for the common "click tabs without scrolling" case.
      if (cached[key] === pos) {
        return;
      }

      cached[key] = pos;
      sessionStorage.setItem(storageKey, JSON.stringify(cached));
    } catch {
      // Ignore quota / security errors.
    }
  };

  const prevScrollRestoration = history.scrollRestoration;

  try {
    history.scrollRestoration = "manual";
  } catch {
    // Ignore — some embedded contexts may reject the assignment.
  }

  // Resolve the container lazily on every event so containers mounted AFTER
  // the provider still get correct scroll handling. Falls back to window when
  // the getter is absent or returns null (pre-mount).
  const readPos = (): number => {
    const element = getContainer?.();

    return element ? element.scrollTop : globalThis.scrollY;
  };

  const writePos = (top: number): void => {
    const element = getContainer?.();

    if (element) {
      element.scrollTo({ top, left: 0, behavior });
    } else {
      globalThis.scrollTo({ top, left: 0, behavior });
    }
  };

  // Restore path (back / traverse / reload). Unlike `writePos`, this tolerates a
  // scroll container that both MOUNTS and LAYS OUT a few frames AFTER the
  // navigation settles.
  //
  // The capture-side `readPos` always runs against an already-mounted DOM (the
  // route being left). On restore the target route — and its container — is
  // still being committed by the view layer. The subscribe callback schedules a
  // single rAF; for a heavy route (e.g. a long virtual list) the framework's
  // commit can land AFTER that frame. Two distinct failures follow, each losing
  // the saved position (Scenario 6 e2e, reproduced under CI's slower runner):
  //
  //   1. Container not mounted yet → `getContainer()` is `null`, the scroll
  //      silently falls back to `window`, which on a container-only route has
  //      nothing to scroll.
  //   2. Container mounted but its content not laid out yet → `scrollHeight`
  //      is still small, so a single `scrollTo({ top })` clamps short of the
  //      saved position and never re-applies once layout grows.
  //
  // With no `scrollContainer` getter the target is always `window`, present
  // from the first frame — restore in a single shot (unchanged behaviour). When
  // a getter is configured we cannot tell "this route legitimately uses window"
  // from "the container is still mounting", so re-apply the scroll on every
  // frame for a bounded budget: window as a fallback while the container is
  // absent (harmless clamp on container routes), the container itself once it
  // appears. For instant restores we stop early the moment the position sticks;
  // smooth restores animate asynchronously, so they run the full budget. The
  // frame budget is the hard backstop against an unreachable target (saved
  // position taller than the restored content).
  let restoreToken = 0;

  const restorePos = (top: number): void => {
    if (!getContainer) {
      globalThis.scrollTo({ top, left: 0, behavior });

      return;
    }

    let frames = 0;
    // ⚑ A per-restore token, the idea `view-transitions.ts` carries as
    // `scheduledVT` (#781) and this file already carries as `scrollSettled` on
    // the capture side (#782). Without it the budget is gated by `destroyed`
    // alone, so a loop whose target is unreachable — a container that clamps
    // short and keeps retrying — is still running when the next navigation
    // lands, and writes the PREVIOUS route's offset onto the current page the
    // moment that container's layout grows (#1924).
    const token = (restoreToken += 1);

    const attempt = (): void => {
      if (destroyed || token !== restoreToken) {
        return;
      }

      const element = getContainer();

      if (element) {
        element.scrollTo({ top, left: 0, behavior });

        // Instant restore landed within rounding tolerance → done; no point
        // re-applying. Smooth restore never matches synchronously, so let it
        // ride the budget.
        if (behavior !== "smooth" && Math.abs(element.scrollTop - top) <= 1) {
          return;
        }
      } else {
        globalThis.scrollTo({ top, left: 0, behavior });
      }

      if (frames >= RESTORE_RETRY_FRAMES) {
        return;
      }

      frames += 1;
      requestAnimationFrame(attempt);
    };

    attempt();
  };

  const scrollToHashOrTop = (route: State): void => {
    // URL plugin path (#532): `state.context.url.hash` is the source of truth
    // when one of the URL plugins (browser-plugin / navigation-plugin) is
    // installed. The value is already DECODED — feeding it through
    // `decodeURIComponent` again would throw on a bare `%`.
    const ctxHash = (route.context as { url?: { hash?: string } } | undefined)
      ?.url?.hash;

    if (ctxHash !== undefined) {
      if (anchorEnabled && ctxHash.length > 0) {
        // eslint-disable-next-line unicorn/prefer-query-selector -- ids may contain CSS-unsafe chars
        const element = document.getElementById(ctxHash);

        if (element) {
          element.scrollIntoView({ behavior });

          return;
        }
      }

      writePos(0);

      return;
    }

    // Fallback path: no URL plugin, read the DOM. `location.hash` is
    // percent-encoded; ids in the DOM are the raw string, so decode for the
    // match. Fall back to the raw slice if the hash contains a malformed
    // escape sequence (decodeURIComponent throws on those).
    const hash = globalThis.location.hash;

    if (anchorEnabled && hash.length > 1) {
      let id: string;

      try {
        id = decodeURIComponent(hash.slice(1));
      } catch {
        id = hash.slice(1);
      }

      // eslint-disable-next-line unicorn/prefer-query-selector -- ids may contain CSS-unsafe chars
      const element = document.getElementById(id);

      if (element) {
        element.scrollIntoView({ behavior });

        return;
      }
    }

    writePos(0);
  };

  let destroyed = false;
  // Capture/effect seam guard (#782). previousRoute's position is captured
  // synchronously in `subscribe`, but the snap/restore effect runs a frame
  // later in rAF. Across that window the viewport still shows the route BEFORE
  // previousRoute, so a second navigation landing in the same frame would
  // capture that foreign position under previousRoute's key. `scrollSettled` is
  // false across the window — capture is skipped (previousRoute's own stored
  // value survives the transit). A real user scroll in this <16ms window is
  // physically impossible.
  let scrollSettled = true;

  const unsubscribe = router.subscribe(({ route, previousRoute }) => {
    const nav = (route.context as { navigation?: NavigationContext })
      .navigation;

    // Browsers dispatch reload as the initial navigation after refresh, so
    // previousRoute is undefined and capture is naturally skipped. The
    // pre-refresh position was already persisted via pagehide. Capture is also
    // skipped while the scroll is unsettled — a second navigation in the same
    // frame, before the prior nav's rAF snap (see `scrollSettled`, #782).
    if (previousRoute && scrollSettled) {
      putPos(keyOf(previousRoute), readPos());
    }

    // This navigation's scroll effect is now pending: the viewport position no
    // longer belongs to `route` until the rAF below runs and settles it.
    scrollSettled = false;

    requestAnimationFrame(() => {
      if (destroyed) {
        return;
      }

      // Effect running — the position now belongs to `route`, so the next
      // capture is honest again.
      scrollSettled = true;

      if (mode === "top") {
        scrollToHashOrTop(route);

        return;
      }

      // Restore branches (reload, back/traverse) MUST be evaluated before the
      // replace-skip below. Since #657 lifted `replace` into TransitionMeta, a
      // history TRAVERSAL (back/forward) under navigation-plugin carries
      // `transition.replace === true` — a traversal reuses an existing history
      // entry, which is replace-shaped at the history level. If the replace-skip
      // ran first it would swallow every back/forward navigation and restore
      // would never fire (the Scenario 6 e2e regression). Genuine in-place
      // replaces (`router.navigate({ replace: true })`, navigateToNotFound) are
      // not traversals and fall through to the skip below.
      //
      // Both arms of each check are required: `transition.reload` only fires for
      // programmatic `router.navigate({reload:true})`. F5 under navigation-plugin
      // primes `nav.navigationType === "reload"` via #531 getActivationType but
      // leaves opts.reload undefined, so dropping the plugin arm would regress F5
      // scroll-restore. Browser-plugin's F5 is not covered (no priming, out of
      // scope).
      // `?.` on a required field: core commits a foreign State's ABSENT
      // `transition` rather than fabricating one (#1792 / #1976), and this runs
      // on whatever `subscribe` hands over. Absent falls through to the plugin
      // arm, which is the pre-#1976 answer for a state carrying no meta.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- the required field is genuinely absent on a foreign committed State
      if (route.transition?.reload || nav?.navigationType === "reload") {
        restorePos(loadStore()[keyOf(route)] ?? 0);

        return;
      }

      if (nav?.direction === "back" || nav?.navigationType === "traverse") {
        restorePos(loadStore()[keyOf(route)] ?? 0);

        return;
      }

      // Genuine in-place replace (not a traversal) — leave scroll untouched.
      // `?.` for the same reason as the reload arm above.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- the required field is genuinely absent on a foreign committed State
      if (route.transition?.replace || nav?.navigationType === "replace") {
        return;
      }

      scrollToHashOrTop(route);
    });
  });

  const onPageHide = (): void => {
    const current = router.getState();

    if (current) {
      putPos(keyOf(current), readPos());
    }
  };

  globalThis.addEventListener("pagehide", onPageHide);

  return {
    destroy: () => {
      // No `if (destroyed) return` guard: every teardown below is idempotent —
      // `unsubscribe()` is a `set.delete` in the core EventEmitter, DOM
      // `removeEventListener` is spec-idempotent, and the history assignment is
      // a plain re-set. There is no ref-count to protect (unlike
      // route-announcer's shared announcer element), so a double `destroy()` is
      // harmless. `destroyed = true` still gates any pending restore rAF / retry.
      destroyed = true;
      unsubscribe();
      globalThis.removeEventListener("pagehide", onPageHide);

      try {
        history.scrollRestoration = prevScrollRestoration;
      } catch {
        // Ignore.
      }
    },
  };
}

/**
 * Internal cache-key builder for scroll-position storage.
 *
 * **Exported for testing only — not part of the public API** (intentionally
 * excluded from `index.ts` barrel). Adapter property tests import it rather
 * than replicating it (§8b H20 / audit-2026-05-16 #S3): a replica drifts
 * silently the moment the key changes. A change to the key format loses saved
 * positions across an upgrade, so the test set is the contract.
 *
 * ## Not memoized
 *
 * The key is a string property read, which is cheaper than the `WeakMap`
 * lookup any cache would need — so there is nothing here to cache.
 */
export function keyOf(state: State): string {
  // The key is the LOCATION, and `state.path` is the form core prints it in
  // (#1923). Deriving it from the bags instead makes this a SECOND place that
  // has to know how a value prints — and the two domains disagree there: the
  // URL direction parses `?page=2` into the number `2`, an intent keeps `"2"`,
  // and `packages/core/src/helpers.ts` says comparison is the single place that
  // knows they describe one location. Reading the printed form asks core rather
  // than re-deriving it, so a route's query, its `?id` carve-out twin and the
  // order its params were written in are all already settled here.
  return state.path;
}
